/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * PERFORMANCE OPTIMIZATIONS
 */

// Lazy load heavy libraries
function lazyLoadScript(src, callback) {
  const script = document.createElement('script');
  script.src = src;
  script.defer = true;
  if (callback) script.onload = callback;
  document.head.appendChild(script);
}

// Load Flickity only when needed
function loadFlickityWhenNeeded() {
  const productMedia = document.querySelector('.product-media');
  if (productMedia && window.innerWidth > 999) {
    lazyLoadScript(window.theme.assets.flickity);
  }
}

// Load PhotoSwipe only when zoom is clicked
function loadPhotoSwipeOnDemand() {
  document.addEventListener('click', function(e) {
    if (e.target.closest('[data-action="zoom"]')) {
      lazyLoadScript(window.theme.assets.photoswipe, function() {
        // PhotoSwipe loaded, trigger zoom
        e.target.click();
      });
    }
  }, { once: true });
}

// Initialize performance optimizations
document.addEventListener('DOMContentLoaded', function() {
  loadFlickityWhenNeeded();
  loadPhotoSwipeOnDemand();
});


// Phrase livraison mini-cart
document.addEventListener("DOMContentLoaded", function() {

  function changePrice() {
    const checkoutButton = document.querySelector('.mini-cart__drawer-footer .checkout-button');
    const price = checkoutButton.childNodes[checkoutButton.childNodes.length - 1].textContent;
    document.querySelector(".drawer__footer .mini-cart__actions .livraison-details .sous-total-price").innerHTML = price;
  }

  function changeLivraisonPhrase() {
    var livraison = document.querySelector("#mini-cart .shipping-bar__text");
    var livraisonElement = document.querySelector("#mini-cart .livraison-details .livraison");
    if ( !!livraison === true && !!livraisonElement === true ){
      var livraisonText = livraison.textContent.replace("bénéficier de","").replace(" en point relais !","");
      if ( livraisonText.includes("Profitez") ){
        livraisonText = "Livraison gratuite";
      }else if ( livraisonText.includes("Enjoy") ){
        livraisonText = "Free shipping";
      }else if ( livraisonText.includes("Disfruta") ){
        livraisonText = "Envío gratuito";
      }
      document.querySelector(".drawer__footer .mini-cart__actions .livraison-details .livraison-price").innerHTML = livraisonText;
    }
  }

  // Variable pour suivre la dernière exécution
let lastExecutionTime = 0;

function addFreeProductCart() {
  // Vérifier si 2 secondes se sont écoulées depuis la dernière exécution
  const currentTime = Date.now();
  if (currentTime - lastExecutionTime < 2000) {
    console.log("Attente de 2 secondes entre les ajouts...");
    return; // Sortir de la fonction si moins de 2 secondes se sont écoulées
  }
  
  var freeProductCart = document.querySelector("#mini-cart .produit-gratuit-icone");
    if (!!freeProductCart === true) {
      var progress = document.querySelector(".shipping-bar").getAttribute("style");
      if (progress == "--progress: 1" || progress == "--progress: 1.0") {
        var variantId = document.querySelector("#mini-cart .produit-gratuit-icone").getAttribute("variant-id");
        if (!!document.querySelector('#mini-cart-form .line-item[first-variant-id="'+ variantId +'"]') === false) {
          // Marquer le temps d'exécution avant de commencer la requête
          lastExecutionTime = currentTime;
          
          console.log("Ajout du produit gratuit...");
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          })
          .then(response => response.json())
          .then(data => {
            return fetch(`${window.themeVariables.routes.cartUrl}.js`);
          })
          .then(response => response.json())
          .then(cartContent => {
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
              bubbles: true,
              detail: {
                cart: cartContent
              }
            }));
          })
          .catch(error => {
            // En cas d'erreur, réinitialiser le temps d'exécution
            lastExecutionTime = 0;
            console.error('Erreur:', error);
          });
        }
      }
    }
  }
  
  document.documentElement.addEventListener('cart:refresh', function(event) {
    setTimeout(function () {
      changePrice();
      changeLivraisonPhrase();
      addFreeProductCart();
    }, 1000);
  });
  
});




// Le widget chat cache le bouton ATC sticky 
var width_screen = window.innerWidth;
if (width_screen <= 1000) {
  if (!!document.querySelector(".product-sticky-form") === true && !!document.querySelector("html body #dummy-chat-button-iframe") === true){
    window.addEventListener('scroll',(event) => {
        if (document.querySelector(".product-sticky-form").hasAttribute("hidden")) {
          document.querySelector("html body #dummy-chat-button-iframe").style.bottom = "24px";
        }else{
          document.querySelector("html body #dummy-chat-button-iframe").style.bottom = "84px";
        }
    });
  }
}

// Open Search Drawer
if (!!document.querySelector(".search__icon") === true){
  var search__icon = document.querySelectorAll('.search__icon');
  for (var i = 0; i < search__icon.length; i++) {
    search__icon[i].addEventListener('click', function(event) {
      document.getElementById('search-drawer').classList.add("open");
      document.querySelector("#search-drawer .drawer__overlay").onclick = function() {
        document.getElementById('search-drawer').classList.remove("open");
      };
      var close_button = document.querySelectorAll('.drawer__close-button');
      for (var i = 0; i < close_button.length; i++) {
      	close_button[i].addEventListener('click', function(event) {
      		document.getElementById('search-drawer').classList.remove("open");
      	});
      }
    });
  }
}


// Delete taille en doublon dans item-product
if (!!document.querySelector(".product-item__info .variants-taille li[name]") === true){
  document.querySelectorAll('.product-item__info .variants-taille').forEach(function(item) {
    if (!!item.querySelector("li[name]") === true){
      var ulElement = item.querySelector('ul');
      var liElements = ulElement.querySelectorAll('li');
      var liArray = Array.from(liElements);
      var uniqueLiArray = [];
      
      liArray.forEach(function(li) {
        var name = li.getAttribute('name');
        if (!uniqueLiArray.includes(name)) {
            uniqueLiArray.push(name);
        }
      });
  
      while (ulElement.firstChild) {
        ulElement.removeChild(ulElement.firstChild);
      }
  
      uniqueLiArray.forEach(function(name) {
        var liElement = document.createElement('li');
        liElement.setAttribute('name', name);
        liElement.textContent = name;
        ulElement.appendChild(liElement);
      });
    }
  });
}

if (!!document.querySelector('collapsible-content[id="facet-filter-filter.v.availability"]') === true){
  document.querySelectorAll('collapsible-content[id="facet-filter-filter.v.availability"] label').forEach(function(label) {
    label.innerHTML = label.innerHTML.replace(/\([^()]*\)/g, '');
  });
}

// Greenable
function greenableFunction() {
  if (!!document.querySelector('.collapsible-toggle[aria-controls="facet-filter-filter.p.m.custom.filtre_Greenable"]') === true){
    var filterGreenable = document.querySelector('.collapsible-toggle[aria-controls="facet-filter-filter.p.m.custom.filtre_Greenable"]');
    filterGreenable.parentElement.style.cssText = "display: none;";
    var checked = document.querySelector('.checkbox-container .checkbox[name="filter.p.m.custom.filtre_Greenable"]').getAttribute("checked");
    filterGreenable.parentElement.insertAdjacentHTML('afterend', '<div class="product-facet__filter-item greenable"><span class="filter-title">Greenable</span><span class="toggle '+ checked +'"></span></div>');
    document.querySelector(".product-facet__filter-item.greenable .toggle").onclick = function() {
      if ( document.querySelector(".product-facet__filter-item.greenable .toggle").classList.contains("checked") === true ){
        document.querySelector(".product-facet__filter-item.greenable .toggle").classList.remove("checked");
        document.querySelector('.checkbox-container .checkbox[name="filter.p.m.custom.filtre_Greenable"]').click();
        setTimeout(function () {
          greenableFunction();
        }, (1000));
      }else{
        document.querySelector(".product-facet__filter-item.greenable .toggle").classList.add("checked");
        document.querySelector('.checkbox-container .checkbox[name="filter.p.m.custom.filtre_Greenable"]').click();
        setTimeout(function () {
          greenableFunction();
        }, (1000));
      }
    };
  }
}
greenableFunction();


// Correction zoom images
var width_screen = window.innerWidth;
if (width_screen >= 1000) {
  if (!!document.querySelector(".product__zoom-button") === true){
    document.querySelector(".product__zoom-button").onclick = function() {
      setTimeout(function () {
        document.querySelector(".product--thumbnails-left .product__media").style.position = "relative";
      }, 300);
    };
    document.querySelector(".pswp__button.pswp__button--close").onclick = function() {
      document.querySelector(".product--thumbnails-left .product__media").style.position = "sticky";
    };
  }
}

// Banner collection
if (width_screen <= 1000) {
  if (!!document.querySelector(".shopify-section--collection-banner img") === true){
    document.head.insertAdjacentHTML('beforeend', '<style>#main{display: flex;flex-direction: column;}#mobile-facet-toolbar{order: 1;}.shopify-section.shopify-section--collection-banner{order: 0;}.shopify-section.shopify-section--main-collection{order: 2;}</style>');
  }
}

// Menu secondaire (onglets Mode / Maison du menu mobile)
// Blush : ce bloc ne s'exécute que si les deux onglets sont présents. Sans cette vérification, il plante quand
// les onglets sont retirés (menu unique) et bloque tout le reste du script, dont la navigation du menu mobile.
if (document.querySelectorAll("#mobile-menu-drawer .sous-menu li span").length >= 2){
  document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[0].onclick = function() {
    if ( document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[0].querySelector("span").classList.contains("active") === false ){
      document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[1].querySelector("span").classList.remove("active");
      document.querySelector(".mobile-nav.active").classList.remove("active");
      document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[0].querySelector("span").classList.add("active");
      document.querySelector(".mobile-nav.mode").classList.add("active");
    }
  };
  document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[1].onclick = function() {
    if ( document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[1].querySelector("span").classList.contains("active") === false ){
      document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[0].querySelector("span").classList.remove("active");
      document.querySelector(".mobile-nav.active").classList.remove("active");
      document.querySelectorAll("#mobile-menu-drawer .sous-menu li")[1].querySelector("span").classList.add("active");
      document.querySelector(".mobile-nav.lifestyle").classList.add("active");
    }
  };
}


if (!!document.querySelector('.mobile-nav:not(:first-child) .mobile-nav__item[data-level="2"] collapsible-content') === true){
  var dataLevel3Menu = document.querySelectorAll('.mobile-nav:not(:first-child) .mobile-nav__item[data-level="2"] .mobile-nav__link');
  for (var i = 0; i < dataLevel3Menu.length; i++) {
    dataLevel3Menu[i].addEventListener('click', function(event) {
      if (event.currentTarget.parentElement.querySelector('collapsible-content').hasAttribute("open")) {
        event.currentTarget.parentElement.querySelector('collapsible-content').removeAttribute('open');
      }else{
        event.currentTarget.parentElement.querySelector('collapsible-content').setAttribute("open", "");
      }
    });
  }
}




/* NEW MENU MOBILE */

document.addEventListener("DOMContentLoaded", function() {

  // LEVEL 1
  // Blush : uniquement les catégories qui ont un sous-menu (bouton). Les liens simples ouvrent directement leur page.
  var nav__link = document.querySelectorAll(".drawer__content>.mobile-nav>.mobile-nav__item>button.mobile-nav__link");
  for (var i = 0; i < nav__link.length; i++) {
    nav__link[i].addEventListener('click', function(event) {
      var currentTarget = event.currentTarget;
      setTimeout(function () {
        document.querySelector("#mobile-menu-drawer").classList.add("open-1");
      }, 0);
    });
  }

  // LEVEL 1 - RETOUR
  var retour = document.querySelectorAll('.mobile-nav__item[data-level="1"]>collapsible-content>.retour');
  for (var i = 0; i < retour.length; i++) {
    retour[i].addEventListener('click', function(event) {
      event.currentTarget.closest(".mobile-nav__item").querySelector("button").setAttribute("aria-expanded", "false");
      document.querySelector("#mobile-menu-drawer").classList.remove("open-1");
    });
  }

  // LEVEL 2
  // Blush : uniquement les rubriques qui ont un sous-menu (bouton). Les sous-catégories simples ouvrent directement leur collection.
  var nav__link_2 = document.querySelectorAll('.mobile-nav .mobile-nav .mobile-nav__item[data-level="2"]>button.mobile-nav__link');
  for (var i = 0; i < nav__link_2.length; i++) {
    nav__link_2[i].addEventListener('click', function(event) {
      var currentTarget_2 = event.currentTarget;
      setTimeout(function () {
        currentTarget_2.setAttribute("aria-expanded", "true");
        document.querySelector("#mobile-menu-drawer").classList.add("open-2");
      }, 0);
    });
  }

  // LEVEL 2 - RETOUR
  var retour = document.querySelectorAll('.mobile-nav__item[data-level="2"]>collapsible-content>.retour');
  for (var i = 0; i < retour.length; i++) {
    retour[i].addEventListener('click', function(event) {
      event.currentTarget.closest(".mobile-nav__item").querySelector("button").setAttribute("aria-expanded", "false");
      document.querySelector("#mobile-menu-drawer").classList.remove("open-2");
    });
  }


  var sous_menus = document.querySelectorAll('.sous-menu li:not(.menu-mobile-home)');
  for (var i = 0; i < sous_menus.length; i++) {
    sous_menus[i].addEventListener('click', function(event) {
      document.querySelector("#mobile-menu-drawer .drawer__content").style.cssText = "transition: all 0s cubic-bezier(0.7, 0, 0.2, 1);";
      if ( document.querySelector("#mobile-menu-drawer").classList.contains("open-1") === true ){
        document.querySelector("#mobile-menu-drawer").classList.remove("open-1");
      }
      if ( document.querySelector("#mobile-menu-drawer").classList.contains("open-2") === true ){
        document.querySelector("#mobile-menu-drawer").classList.remove("open-2");
      }
      setTimeout(function () {
        document.querySelector("#mobile-menu-drawer .drawer__content").removeAttribute("style");
      }, 500);

      
      document.querySelectorAll('#mobile-menu-drawer .collapsible[open]').forEach(function(collapsible) {
        collapsible.removeAttribute("open");
      });
    });
  }


  document.querySelector(".header__icon-list button.header__icon-wrapper.tap-area").onclick = function() {
    const overlay = document.createElement('span');
    overlay.className = "drawer__overlay new-overlay";
    overlay.style.opacity = "0";
    
    overlay.onclick = function() {
      this.style.opacity = "0";
      setTimeout(() => {
        this.remove();
      }, 600);
    };
    
    document.querySelector("#mobile-menu-drawer").insertAdjacentElement("afterend", overlay);
    overlay.offsetHeight;
    overlay.style.opacity = "0.3";
  };


  
  

  
});






